/**
 * PSP/CRE Tool v2.0 - Medical Assets Auto-Allocation
 * 
 * Purpose: Define automatic allocation of medical evacuation assets by force echelon
 * with Ukraine 2022-2024 validated requirements and icons.js integration.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 21, 2024
 * Version: 2.0 - Enhanced with icons.js integration and Ukraine data
 * 
 * Ground Assets:
 * - Light ambulances: Unarmored, higher speed, lower protection
 * - Armored ambulances: Lower speed, higher protection for contested areas
 * 
 * Air Assets:
 * - IAR-330 Puma: Romanian primary MEDEVAC
 * - UH-60 Black Hawk: NATO/US primary MEDEVAC
 * - CH-47 Chinook: Heavy lift, mass casualty
 * 
 * References:
 * - NATO STANAG 2542 (AJMedP-1) Allied Joint Medical Planning
 * - US Army ATP 4-02.2 Medical Evacuation
 * - Romanian Army Medical Doctrine
 * - Ukraine 2022-2024 MEDEVAC operational data
 * 
 * Usage:
 * import { 
 *   AUTO_ALLOCATION,
 *   getAllocationByStandard,
 *   calculateEvacuationCapacity,
 *   calculateRequiredAssets
 * } from '../constants/medicalAssets';
 */

import { TRANSPORT_ICONS, STATUS_ICONS, MILITARY_ICONS } from '../utils/icons';

// ============================================================================
// LEGACY FORMAT (Backward Compatibility)
// ============================================================================

/**
 * Original simplified format - maintained for backward compatibility
 */
export const AUTO_ALLOCATION = {
  BATTALION: { 
    ground: 4,    // 2 light + 2 armored
    air: 0        // No helicopters at battalion level
  },
  BRIGADE: { 
    ground: 16,   // 8 light + 8 armored
    air: 2        // 1 primary + 1 secondary
  },
  DIVISION: { 
    ground: 60,   // 30 light + 30 armored
    air: 20       // 9 primary + 9 secondary + 2 heavy lift
  },
  CORPS: { 
    ground: 140,  // 70 light + 70 armored
    air: 60       // 28 primary + 28 secondary + 4 heavy lift
  }
};

// ============================================================================
// ENHANCED FORMAT (Detailed Breakdown)
// ============================================================================

/**
 * Detailed allocation by specific asset type
 */
export const AUTO_ALLOCATION_DETAILED = {
  BATTALION: {
    echelon: 'BATTALION',
    label: 'Battalion',
    icon: MILITARY_ICONS.BATTALION,
    
    ground: {
      light: 2,
      armored: 2,
      total: 4,
      icons: {
        light: TRANSPORT_ICONS.AMBULANCE_LIGHT,
        armored: TRANSPORT_ICONS.AMBULANCE_ARMORED
      }
    },
    air: {
      iar330: 0,
      uh60: 0,
      ch47: 0,
      total: 0,
      icons: {
        iar330: TRANSPORT_ICONS.HELICOPTER,
        uh60: TRANSPORT_ICONS.HELICOPTER,
        ch47: TRANSPORT_ICONS.HELICOPTER_HEAVY
      }
    },
    
    capacity: {
      ground_patients_per_trip: 16,    // 4 ambulances × 4 patients
      air_patients_per_hour: 0,
      total_daily_capacity: 96         // Assuming 6 trips per ambulance
    },
    
    ukraine_data: {
      observed_allocation: { light: 2, armored: 3, air: 0 },
      notes: 'Ukraine increased armored ambulances due to drone threat',
      lessons: [
        'More armored vehicles needed in contested environment',
        'Light ambulances only for rear area evacuation',
        'Civilian vehicles often used to supplement'
      ]
    }
  },
  
  BRIGADE: {
    echelon: 'BRIGADE',
    label: 'Brigade',
    icon: MILITARY_ICONS.BRIGADE,
    
    ground: {
      light: 8,
      armored: 8,
      total: 16,
      icons: {
        light: TRANSPORT_ICONS.AMBULANCE_LIGHT,
        armored: TRANSPORT_ICONS.AMBULANCE_ARMORED
      }
    },
    air: {
      iar330: 1,
      uh60: 1,
      ch47: 0,
      total: 2,
      icons: {
        iar330: TRANSPORT_ICONS.HELICOPTER,
        uh60: TRANSPORT_ICONS.HELICOPTER,
        ch47: TRANSPORT_ICONS.HELICOPTER_HEAVY
      }
    },
    
    capacity: {
      ground_patients_per_trip: 64,    // 16 ambulances × 4 patients
      air_patients_per_hour: 12,       // 2 helicopters × 6 patients
      total_daily_capacity: 480        // Ground + air combined
    },
    
    ukraine_data: {
      observed_allocation: { light: 6, armored: 12, air: 2 },
      notes: 'Armored ambulances became standard for forward evacuation',
      lessons: [
        'MEDEVAC helicopters rarely used in contested airspace',
        'Ground evacuation primary method',
        'Night helicopter ops only in high-value cases'
      ]
    },
    
    // Note: Helicopters not organic to brigade - allocated from higher
    helicopter_note: 'MEDEVAC helicopters allocated from Romanian Air Force, ' +
                     'US Division CAB, or NATO MOUs - not organic to brigade'
  },
  
  DIVISION: {
    echelon: 'DIVISION',
    label: 'Division',
    icon: MILITARY_ICONS.DIVISION,
    
    ground: {
      light: 30,
      armored: 30,
      total: 60,
      icons: {
        light: TRANSPORT_ICONS.AMBULANCE_LIGHT,
        armored: TRANSPORT_ICONS.AMBULANCE_ARMORED
      }
    },
    air: {
      iar330: 9,
      uh60: 9,
      ch47: 2,
      total: 20,
      icons: {
        iar330: TRANSPORT_ICONS.HELICOPTER,
        uh60: TRANSPORT_ICONS.HELICOPTER,
        ch47: TRANSPORT_ICONS.HELICOPTER_HEAVY
      }
    },
    
    capacity: {
      ground_patients_per_trip: 240,
      air_patients_per_hour: 156,      // 18 standard + 2 CH-47
      total_daily_capacity: 2400
    },
    
    ukraine_data: {
      observed_allocation: { light: 20, armored: 50, air: 8 },
      notes: 'Division-level air assets severely limited by AD threat',
      lessons: [
        'Helicopter availability 30-40% of NATO planning factors',
        'Ground evacuation chain extended to 100+ km',
        'Train evacuation essential alternative',
        'Armored ambulances 2.5:1 ratio over light'
      ]
    }
  },
  
  CORPS: {
    echelon: 'CORPS',
    label: 'Corps',
    icon: MILITARY_ICONS.CORPS,
    
    ground: {
      light: 70,
      armored: 70,
      total: 140,
      icons: {
        light: TRANSPORT_ICONS.AMBULANCE_LIGHT,
        armored: TRANSPORT_ICONS.AMBULANCE_ARMORED
      }
    },
    air: {
      iar330: 28,
      uh60: 28,
      ch47: 4,
      total: 60,
      icons: {
        iar330: TRANSPORT_ICONS.HELICOPTER,
        uh60: TRANSPORT_ICONS.HELICOPTER,
        ch47: TRANSPORT_ICONS.HELICOPTER_HEAVY
      }
    },
    
    capacity: {
      ground_patients_per_trip: 560,
      air_patients_per_hour: 432,
      total_daily_capacity: 6000
    },
    
    ukraine_data: {
      observed_allocation: { light: 50, armored: 120, air: 20 },
      notes: 'Corps-level air assets concentrated in rear areas',
      lessons: [
        'Strategic air evacuation more important than tactical',
        'Fixed-wing MEDEVAC essential for Role 3→4',
        'Train medical evacuation critical capability',
        'International MEDEVAC support required'
      ]
    }
  }
};

// ============================================================================
// NATIONAL STANDARD-SPECIFIC ALLOCATION
// ============================================================================

/**
 * Allocation adjusted by national doctrine
 */
export const AUTO_ALLOCATION_BY_NATIONAL_STANDARD = {
  ROMANIAN: {
    label: 'Romanian Army Standard',
    icon: '🇷🇴',
    primary_helicopter: 'IAR-330',
    
    BATTALION: {
      ground: { light: 2, armored: 2, total: 4 },
      air: { iar330: 0, uh60: 0, ch47: 0, total: 0 }
    },
    BRIGADE: {
      ground: { light: 8, armored: 8, total: 16 },
      air: { iar330: 2, uh60: 0, ch47: 0, total: 2 }
    },
    DIVISION: {
      ground: { light: 30, armored: 30, total: 60 },
      air: { iar330: 18, uh60: 0, ch47: 2, total: 20 }
    },
    CORPS: {
      ground: { light: 70, armored: 70, total: 140 },
      air: { iar330: 56, uh60: 0, ch47: 4, total: 60 }
    }
  },
  
  NATO: {
    label: 'NATO Standard',
    icon: '🌐',
    primary_helicopter: 'MIXED',
    
    BATTALION: {
      ground: { light: 2, armored: 2, total: 4 },
      air: { iar330: 0, uh60: 0, ch47: 0, total: 0 }
    },
    BRIGADE: {
      ground: { light: 8, armored: 8, total: 16 },
      air: { iar330: 1, uh60: 1, ch47: 0, total: 2 }
    },
    DIVISION: {
      ground: { light: 30, armored: 30, total: 60 },
      air: { iar330: 9, uh60: 9, ch47: 2, total: 20 }
    },
    CORPS: {
      ground: { light: 70, armored: 70, total: 140 },
      air: { iar330: 28, uh60: 28, ch47: 4, total: 60 }
    }
  },
  
  US: {
    label: 'US Army Standard',
    icon: '🇺🇸',
    primary_helicopter: 'UH-60',
    
    BATTALION: {
      ground: { light: 2, armored: 2, total: 4 },
      air: { iar330: 0, uh60: 0, ch47: 0, total: 0 }
    },
    BRIGADE: {
      ground: { light: 8, armored: 8, total: 16 },
      air: { iar330: 0, uh60: 2, ch47: 0, total: 2 }
    },
    DIVISION: {
      ground: { light: 30, armored: 30, total: 60 },
      air: { iar330: 0, uh60: 18, ch47: 2, total: 20 }
    },
    CORPS: {
      ground: { light: 70, armored: 70, total: 140 },
      air: { iar330: 0, uh60: 56, ch47: 4, total: 60 }
    }
  },
  
  UKRAINE_RECOMMENDED: {
    label: 'Ukraine 2022-2024 Recommended',
    icon: '🇺🇦',
    primary_helicopter: 'MIXED',
    notes: 'Adjusted based on actual Ukraine operational experience',
    
    BATTALION: {
      ground: { light: 1, armored: 4, total: 5 },  // More armored
      air: { iar330: 0, uh60: 0, ch47: 0, total: 0 }
    },
    BRIGADE: {
      ground: { light: 4, armored: 14, total: 18 },  // 3.5:1 armored ratio
      air: { iar330: 1, uh60: 1, ch47: 0, total: 2 }
    },
    DIVISION: {
      ground: { light: 15, armored: 50, total: 65 },  // Heavy armored focus
      air: { iar330: 4, uh60: 4, ch47: 2, total: 10 }  // Reduced air
    },
    CORPS: {
      ground: { light: 40, armored: 120, total: 160 },
      air: { iar330: 10, uh60: 10, ch47: 4, total: 24 }  // Much reduced air
    }
  }
};

// ============================================================================
// HELICOPTER SPECIFICATIONS
// ============================================================================

export const HELICOPTER_SPECS = {
  IAR330: {
    code: 'IAR330',
    name: 'IAR-330 Puma',
    country: 'Romania',
    icon: TRANSPORT_ICONS.HELICOPTER,
    flag: '🇷🇴',
    
    capacity: {
      litter: 6,
      ambulatory: 10,
      mixed: 4  // 4 litter + 4 ambulatory
    },
    
    performance: {
      speed_kmh: 230,
      range_km: 550,
      turnaround_min: 15
    },
    
    crew: {
      pilots: 2,
      medics: 2,
      total: 4
    }
  },
  
  UH60: {
    code: 'UH60',
    name: 'UH-60 Black Hawk',
    country: 'USA/NATO',
    icon: TRANSPORT_ICONS.HELICOPTER,
    flag: '🇺🇸',
    
    capacity: {
      litter: 6,
      ambulatory: 12,
      mixed: 4
    },
    
    performance: {
      speed_kmh: 280,
      range_km: 590,
      turnaround_min: 12
    },
    
    crew: {
      pilots: 2,
      medics: 2,
      total: 4
    }
  },
  
  CH47: {
    code: 'CH47',
    name: 'CH-47 Chinook',
    country: 'USA/NATO',
    icon: TRANSPORT_ICONS.HELICOPTER_HEAVY,
    flag: '🇺🇸',
    
    capacity: {
      litter: 24,
      ambulatory: 33,
      mixed: 20
    },
    
    performance: {
      speed_kmh: 260,
      range_km: 740,
      turnaround_min: 20
    },
    
    crew: {
      pilots: 2,
      medics: 4,
      crew_chiefs: 2,
      total: 8
    },
    
    notes: 'Heavy lift for mass casualty evacuation'
  }
};

// ============================================================================
// AMBULANCE SPECIFICATIONS
// ============================================================================

export const AMBULANCE_SPECS = {
  LIGHT: {
    code: 'LIGHT',
    name: 'Light Ambulance',
    icon: TRANSPORT_ICONS.AMBULANCE_LIGHT,
    
    capacity: {
      litter: 4,
      ambulatory: 6
    },
    
    performance: {
      speed_paved_kmh: 80,
      speed_unpaved_kmh: 40,
      range_km: 500
    },
    
    protection: 'NONE',
    terrain: ['PAVED', 'IMPROVED'],
    
    crew: {
      driver: 1,
      medic: 1,
      total: 2
    },
    
    notes: 'Best for rear area evacuation, higher speed'
  },
  
  ARMORED: {
    code: 'ARMORED',
    name: 'Armored Ambulance',
    icon: TRANSPORT_ICONS.AMBULANCE_ARMORED,
    
    capacity: {
      litter: 4,
      ambulatory: 4
    },
    
    performance: {
      speed_paved_kmh: 60,
      speed_unpaved_kmh: 30,
      range_km: 400
    },
    
    protection: 'STANAG 4569 Level 2',
    terrain: ['ALL'],
    
    crew: {
      driver: 1,
      medic: 1,
      total: 2
    },
    
    notes: 'Essential for forward evacuation in contested areas'
  },
  
  TRACKED: {
    code: 'TRACKED',
    name: 'Tracked Ambulance (M113)',
    icon: TRANSPORT_ICONS.AMBULANCE_ARMORED,
    
    capacity: {
      litter: 4,
      ambulatory: 2
    },
    
    performance: {
      speed_paved_kmh: 50,
      speed_unpaved_kmh: 35,
      range_km: 300
    },
    
    protection: 'STANAG 4569 Level 3',
    terrain: ['ALL', 'CROSS_COUNTRY'],
    
    crew: {
      driver: 1,
      medic: 1,
      total: 2
    },
    
    notes: 'Best for cross-country and forward area evacuation'
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get auto-allocated medical assets for echelon (legacy format)
 * @param {string} echelon - Force echelon
 * @returns {Object} Simple allocation { ground, air }
 */
export function getAutoAllocation(echelon) {
  return AUTO_ALLOCATION[echelon] || AUTO_ALLOCATION.BRIGADE;
}

/**
 * Get detailed allocation for echelon
 * @param {string} echelon - Force echelon
 * @returns {Object} Detailed allocation
 */
export function getDetailedAllocation(echelon) {
  return AUTO_ALLOCATION_DETAILED[echelon] || AUTO_ALLOCATION_DETAILED.BRIGADE;
}

/**
 * Get allocation by echelon and national standard
 * @param {string} echelon - Force echelon
 * @param {string} nationalStandard - ROMANIAN, NATO, US, or UKRAINE_RECOMMENDED
 * @returns {Object} Allocation for standard
 */
export function getAllocationByStandard(echelon, nationalStandard) {
  const standard = nationalStandard || 'NATO';
  const allocations = AUTO_ALLOCATION_BY_NATIONAL_STANDARD[standard];
  
  if (!allocations || !allocations[echelon]) {
    return AUTO_ALLOCATION_DETAILED[echelon];
  }
  
  return {
    ...allocations[echelon],
    echelon: echelon,
    standard: standard,
    label: allocations.label,
    icon: allocations.icon
  };
}

/**
 * Calculate evacuation capacity
 * @param {string} echelon - Force echelon
 * @param {string} nationalStandard - National standard
 * @returns {Object} Capacity analysis
 */
export function calculateEvacuationCapacity(echelon, nationalStandard = null) {
  const allocation = nationalStandard ? 
    getAllocationByStandard(echelon, nationalStandard) : 
    getDetailedAllocation(echelon);
  
  // Ground capacity: 4 patients per ambulance, 6 trips per day
  const groundCapacityPerTrip = allocation.ground.total * 4;
  const groundCapacityDaily = groundCapacityPerTrip * 6;
  
  // Air capacity by helicopter type
  const iar330Capacity = (allocation.air.iar330 || 0) * 6;
  const uh60Capacity = (allocation.air.uh60 || 0) * 6;
  const ch47Capacity = (allocation.air.ch47 || 0) * 24;
  const airCapacityPerHour = iar330Capacity + uh60Capacity + ch47Capacity;
  const airCapacityDaily = airCapacityPerHour * 12;  // 12 hours ops
  
  return {
    ground: {
      per_trip: groundCapacityPerTrip,
      daily: groundCapacityDaily,
      light_contribution: allocation.ground.light * 4 * 6,
      armored_contribution: allocation.ground.armored * 4 * 6
    },
    air: {
      per_hour: airCapacityPerHour,
      daily: airCapacityDaily,
      iar330_contribution: (allocation.air.iar330 || 0) * 6 * 12,
      uh60_contribution: (allocation.air.uh60 || 0) * 6 * 12,
      ch47_contribution: (allocation.air.ch47 || 0) * 24 * 12
    },
    total: {
      daily: groundCapacityDaily + airCapacityDaily,
      icon: STATUS_ICONS.GREEN
    },
    breakdown: {
      light_ambulances: allocation.ground.light * 4,
      armored_ambulances: allocation.ground.armored * 4,
      iar330_helicopters: (allocation.air.iar330 || 0) * 6,
      uh60_helicopters: (allocation.air.uh60 || 0) * 6,
      ch47_helicopters: (allocation.air.ch47 || 0) * 24
    }
  };
}

/**
 * Calculate required assets for expected casualties
 * @param {number} dailyCasualties - Expected daily casualties
 * @param {string} paradigm - PERMISSIVE, CONTESTED, or DENIED
 * @returns {Object} Required assets
 */
export function calculateRequiredAssets(dailyCasualties, paradigm = 'CONTESTED') {
  // Assume 35% require air evacuation in permissive, less in contested
  const airEvacPercent = paradigm === 'PERMISSIVE' ? 0.35 : 
                         paradigm === 'CONTESTED' ? 0.20 : 
                         0.10;  // DENIED
  
  const groundEvacPercent = 1 - airEvacPercent;
  
  // Ground: 4 patients per ambulance, 6 trips per day
  const groundPatients = dailyCasualties * groundEvacPercent;
  const ambulancesNeeded = Math.ceil(groundPatients / 24);  // 4 × 6 trips
  
  // Prefer armored in contested/denied
  const armoredRatio = paradigm === 'PERMISSIVE' ? 0.5 : 
                       paradigm === 'CONTESTED' ? 0.75 : 
                       0.90;
  
  // Air: 6 patients per helicopter per hour, 8-12 hours ops
  const airPatients = dailyCasualties * airEvacPercent;
  const helicopterHoursNeeded = airPatients / 6;
  const helicoptersNeeded = Math.ceil(helicopterHoursNeeded / 10);  // 10 hours per helicopter
  
  return {
    daily_casualties: dailyCasualties,
    paradigm: paradigm,
    ground: {
      total: ambulancesNeeded,
      light: Math.round(ambulancesNeeded * (1 - armoredRatio)),
      armored: Math.round(ambulancesNeeded * armoredRatio),
      patients_handled: groundPatients
    },
    air: {
      total: helicoptersNeeded,
      patients_handled: airPatients,
      note: paradigm === 'DENIED' ? 'Air assets severely limited' : null
    },
    recommendations: {
      icon: ambulancesNeeded > 20 ? STATUS_ICONS.YELLOW : STATUS_ICONS.GREEN,
      notes: paradigm === 'DENIED' 
        ? 'Consider ground-only evacuation plan'
        : paradigm === 'CONTESTED'
          ? 'Night helicopter operations recommended'
          : 'Standard evacuation mix viable'
    }
  };
}

/**
 * Get helicopter type by national standard
 * @param {string} nationalStandard - National standard
 * @returns {Object} Primary helicopter info
 */
export function getHelicopterType(nationalStandard) {
  if (nationalStandard === 'ROMANIAN') {
    return HELICOPTER_SPECS.IAR330;
  }
  return HELICOPTER_SPECS.UH60;
}

/**
 * Get primary helicopter code for national standard
 * @param {string} nationalStandard - National standard
 * @returns {string} Helicopter code
 */
export function getPrimaryHelicopterCode(nationalStandard) {
  if (nationalStandard === 'ROMANIAN') {
    return 'IAR330';
  }
  return 'UH60';
}

/**
 * Split total helicopters by national standard
 * @param {number} totalHelicopters - Total helicopters to split
 * @param {string} nationalStandard - National standard
 * @param {boolean} includeHeavyLift - Include CH-47 allocation
 * @returns {Object} Split by type
 */
export function splitHelicoptersByStandard(totalHelicopters, nationalStandard, includeHeavyLift = false) {
  const heavyLift = includeHeavyLift ? Math.min(Math.floor(totalHelicopters * 0.1), 4) : 0;
  const remainingHelicopters = totalHelicopters - heavyLift;
  
  if (nationalStandard === 'ROMANIAN') {
    return {
      iar330: remainingHelicopters,
      uh60: 0,
      ch47: heavyLift,
      total: totalHelicopters
    };
  } else if (nationalStandard === 'US') {
    return {
      iar330: 0,
      uh60: remainingHelicopters,
      ch47: heavyLift,
      total: totalHelicopters
    };
  } else {
    // NATO mixed fleet
    const perType = Math.floor(remainingHelicopters / 2);
    return {
      iar330: perType,
      uh60: remainingHelicopters - perType,
      ch47: heavyLift,
      total: totalHelicopters
    };
  }
}

/**
 * Validate allocation against standards
 * @param {string} echelon - Force echelon
 * @param {Object} allocation - Proposed allocation
 * @param {string} nationalStandard - National standard
 * @returns {Object} Validation result
 */
export function validateAllocation(echelon, allocation, nationalStandard = 'NATO') {
  const standard = getAllocationByStandard(echelon, nationalStandard);
  const errors = [];
  const warnings = [];
  
  // Validate ground assets
  if (allocation.ground) {
    const standardTotal = standard.ground?.total || 0;
    const allocationTotal = (allocation.ground.light || 0) + (allocation.ground.armored || 0);
    
    if (allocationTotal < standardTotal * 0.5) {
      warnings.push({
        icon: STATUS_ICONS.YELLOW_WARNING,
        message: `Ground ambulances (${allocationTotal}) below 50% of standard (${standardTotal})`
      });
    }
    if (allocationTotal > standardTotal * 2) {
      warnings.push({
        icon: STATUS_ICONS.YELLOW,
        message: `Ground ambulances (${allocationTotal}) exceed 200% of standard (${standardTotal})`
      });
    }
  }
  
  // Validate air assets
  if (allocation.air) {
    const standardTotal = standard.air?.total || 0;
    const allocationTotal = (allocation.air.iar330 || 0) + (allocation.air.uh60 || 0) + (allocation.air.ch47 || 0);
    
    if (standardTotal > 0 && allocationTotal < standardTotal * 0.5) {
      warnings.push({
        icon: STATUS_ICONS.YELLOW_WARNING,
        message: `Helicopters (${allocationTotal}) below 50% of standard (${standardTotal})`
      });
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    icon: errors.length > 0 ? STATUS_ICONS.RED : 
          warnings.length > 0 ? STATUS_ICONS.YELLOW : 
          STATUS_ICONS.GREEN
  };
}

/**
 * Get ground ambulances (legacy)
 * @param {string} echelon - Force echelon
 * @returns {number} Total ground ambulances
 */
export function getGroundAmbulances(echelon) {
  const allocation = AUTO_ALLOCATION[echelon] || AUTO_ALLOCATION.BRIGADE;
  return allocation.ground;
}

/**
 * Get air assets (legacy)
 * @param {string} echelon - Force echelon
 * @returns {number} Total helicopters
 */
export function getAirAssets(echelon) {
  const allocation = AUTO_ALLOCATION[echelon] || AUTO_ALLOCATION.BRIGADE;
  return allocation.air;
}

/**
 * Get detailed ground ambulances
 * @param {string} echelon - Force echelon
 * @param {string} nationalStandard - National standard
 * @returns {Object} Ground breakdown
 */
export function getGroundAmbulancesDetailed(echelon, nationalStandard = null) {
  const allocation = nationalStandard 
    ? getAllocationByStandard(echelon, nationalStandard)
    : getDetailedAllocation(echelon);
  return allocation.ground;
}

/**
 * Get detailed air assets
 * @param {string} echelon - Force echelon
 * @param {string} nationalStandard - National standard
 * @returns {Object} Air breakdown
 */
export function getAirAssetsDetailed(echelon, nationalStandard = null) {
  const allocation = nationalStandard 
    ? getAllocationByStandard(echelon, nationalStandard)
    : getDetailedAllocation(echelon);
  return allocation.air;
}

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const MEDICAL_ASSETS_EDUCATIONAL = {
  key_principles: [
    'Ground ambulances: 4 patients each, 6 trips per day typical',
    'Helicopters: 6 patients standard, 24 for CH-47',
    'Armored ambulances essential in contested environment',
    'Helicopter availability 30-40% of NATO factors in high-threat'
  ],
  
  ukraine_lessons: [
    'Armored:light ratio should be 2.5:1 in contested areas',
    'MEDEVAC helicopters rarely used near front lines',
    'Night-only helicopter operations in most sectors',
    'Civilian vehicles often supplemented military ambulances',
    'Train evacuation critical for mass casualty events'
  ],
  
  planning_factors: [
    'Battalion: 4 ambulances, 0 helicopters (organic)',
    'Brigade: 16 ambulances, 2 helicopters (allocated)',
    'Division: 60 ambulances, 20 helicopters',
    'Corps: 140 ambulances, 60 helicopters'
  ],
  
  common_errors: [
    'Assuming helicopter availability in contested airspace',
    'Not adjusting armored ratio for threat level',
    'Using NATO factors without Ukraine validation',
    'Not planning ground-only contingency'
  ]
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const medicalAssetsConstants = {
  // Legacy format
  AUTO_ALLOCATION,
  
  // Enhanced formats
  AUTO_ALLOCATION_DETAILED,
  AUTO_ALLOCATION_BY_NATIONAL_STANDARD,
  
  // Specifications
  HELICOPTER_SPECS,
  AMBULANCE_SPECS,
  
  // Helper functions
  getAutoAllocation,
  getDetailedAllocation,
  getAllocationByStandard,
  calculateEvacuationCapacity,
  calculateRequiredAssets,
  getHelicopterType,
  getPrimaryHelicopterCode,
  splitHelicoptersByStandard,
  validateAllocation,
  getGroundAmbulances,
  getAirAssets,
  getGroundAmbulancesDetailed,
  getAirAssetsDetailed,
  
  // Educational
  MEDICAL_ASSETS_EDUCATIONAL
};

export default medicalAssetsConstants;
